/**
 * Design tokens for Pediatrack.
 *
 * Source of truth for all colors, spacing, radii, shadows, and typography.
 * MUI components consume `palette`, `shape`, and `typography`; the rest is
 * exposed as CSS custom properties via GlobalStyles.js so legacy `.css`
 * files can read the same values.
 *
 * Color palette is the Tailwind v3 default, formalized:
 *   primary   = sky-500    secondary = indigo-500
 *   success   = emerald-500 warning   = amber-500
 *   error     = rose-500   info      = blue-500
 *   neutrals  = slate-50 … slate-900
 */

const paletteCommon = {
    primary:   { main: "#0ea5e9", light: "#38bdf8", dark: "#0284c7", contrastText: "#ffffff" },
    secondary: { main: "#6366f1", light: "#818cf8", dark: "#4f46e5", contrastText: "#ffffff" },
    success:   { main: "#10b981", light: "#34d399", dark: "#059669", contrastText: "#ffffff" },
    warning:   { main: "#f59e0b", light: "#fbbf24", dark: "#d97706", contrastText: "#0f172a" },
    error:     { main: "#ef4444", light: "#f87171", dark: "#dc2626", contrastText: "#ffffff" },
    info:      { main: "#3b82f6", light: "#60a5fa", dark: "#2563eb", contrastText: "#ffffff" },
};

export const lightPalette = {
    mode: "light",
    ...paletteCommon,
    background: { default: "#f8fafc", paper: "#ffffff" },
    surface:    { 1: "#ffffff", 2: "#f8fafc", 3: "#f1f5f9" },
    border:     { soft: "#f1f5f9", default: "#e2e8f0", strong: "#cbd5e1" },
    text:       { primary: "#0f172a", secondary: "#475569", disabled: "#94a3b8", inverse: "#ffffff" },
};

export const shape = {
    borderRadius: 10, // md default
};

export const radius = {
    sm: 6,
    md: 10,
    lg: 14,
    xl: 20,
    pill: 9999,
};

// Standard top padding for every page rendered inside <Layout />.
// Centralized so the PageHeader → content gap is consistent everywhere.
// See Components/Layout/Layout.css (.layout-content).
export const pagePadding = 24;

export const shadows = [
    "none",
    "0 1px 2px 0 rgba(15,23,42,0.05)",
    "0 1px 3px 0 rgba(15,23,42,0.08), 0 1px 2px 0 rgba(15,23,42,0.04)",
    "0 4px 12px -2px rgba(15,23,42,0.08), 0 2px 4px -2px rgba(15,23,42,0.05)",
    "0 12px 28px -8px rgba(15,23,42,0.12), 0 4px 8px -4px rgba(15,23,42,0.06)",
    "0 25px 60px -12px rgba(15,23,42,0.18), 0 8px 16px -8px rgba(15,23,42,0.08)",
    "0 0 0 3px rgba(14,165,233,0.35)", // focus ring
];

// Elevated shadow used for hero / focal surfaces.
export const shadowElevated =
    "0 24px 48px -12px rgba(15,23,42,0.18), 0 8px 16px -8px rgba(15,23,42,0.08)";

// Accent glow shadows — used on hover for gradient cards.
export const glow = {
    sky: "0 18px 40px -12px rgba(14,165,233,0.45), 0 6px 14px -6px rgba(99,102,241,0.30)",
    emerald: "0 18px 40px -12px rgba(16,185,129,0.45), 0 6px 14px -6px rgba(14,165,233,0.25)",
    hero: "0 28px 56px -16px rgba(14,165,233,0.30), 0 10px 20px -10px rgba(99,102,241,0.20)",
    error: "0 14px 32px -10px rgba(239,68,68,0.40)",
};

// Gradient surfaces used by the home page bento cards and hero.
export const gradients = {
    sky: "linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)",
    emerald: "linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)",
    hero: [
        "radial-gradient(at 8% 12%, rgba(14,165,233,0.18) 0px, transparent 55%)",
        "radial-gradient(at 92% 0%, rgba(99,102,241,0.18) 0px, transparent 50%)",
        "radial-gradient(at 100% 100%, rgba(16,185,129,0.12) 0px, transparent 55%)",
        "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    ].join(", "),
    skySoft: "linear-gradient(135deg, rgba(14,165,233,0.10) 0%, rgba(99,102,241,0.10) 100%)",
    emeraldSoft: "linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(14,165,233,0.10) 100%)",
    pink: "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)",
    pinkSoft: "linear-gradient(135deg, rgba(236,72,153,0.10) 0%, rgba(168,85,247,0.10) 100%)",
    heroPink: [
        "radial-gradient(at 8% 12%, rgba(236,72,153,0.20) 0px, transparent 55%)",
        "radial-gradient(at 92% 0%, rgba(168,85,247,0.20) 0px, transparent 50%)",
        "radial-gradient(at 100% 100%, rgba(244,114,182,0.12) 0px, transparent 55%)",
        "linear-gradient(180deg, #ffffff 0%, #fdf2f8 100%)",
    ].join(", "),
};

export const glass = {
    blur: "blur(16px) saturate(140%)",
    blurSoft: "blur(8px) saturate(120%)",
};

export const spacing = 4; // MUI default: 1 unit = 4px

export const typography = {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h1: { fontWeight: 700, fontSize: "2.25rem", lineHeight: 1.2, letterSpacing: "-0.02em" },
    h2: { fontWeight: 700, fontSize: "1.875rem", lineHeight: 1.25, letterSpacing: "-0.015em" },
    h3: { fontWeight: 700, fontSize: "1.5rem", lineHeight: 1.3, letterSpacing: "-0.01em" },
    h4: { fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.35 },
    h5: { fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: "1rem", lineHeight: 1.5 },
    subtitle1: { fontWeight: 500, fontSize: "0.95rem", lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, fontSize: "0.85rem", lineHeight: 1.5 },
    body1: { fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.55 },
    body2: { fontWeight: 400, fontSize: "0.85rem", lineHeight: 1.55 },
    button: { fontWeight: 600, fontSize: "0.9rem", letterSpacing: "0.01em", textTransform: "none" },
    caption: { fontWeight: 400, fontSize: "0.75rem", lineHeight: 1.4 },
    overline: { fontWeight: 600, fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase" },
};

/**
 * Status badge token pairs. Same shapes as Tailwind's color-* with /20
 * opacity backgrounds. Used by <StatusBadge severity="..."/> and as
 * `--pt-badge-bg` / `--pt-badge-fg` CSS vars.
 */
export const statusBadge = {
    success: { bg: "#dcfce7", fg: "#166534" },
    warning: { bg: "#fef9c3", fg: "#854d0e" },
    danger:  { bg: "#fee2e2", fg: "#991b1b" },
    info:    { bg: "#e0f2fe", fg: "#0369a1" },
    neutral: { bg: "#f1f5f9", fg: "#475569" },
};

export const transitions = {
    fast: "120ms ease",
    base: "180ms ease",
    slow: "260ms ease",
    spring: "420ms cubic-bezier(0.16, 1, 0.3, 1)",
    easeOutExpo: "cubic-bezier(0.16, 1, 0.3, 1)",
};

export function buildCssVars() {
    const p = lightPalette;
    const sb = statusBadge;
    return {
        "--pt-bg": p.background.default,
        "--pt-paper": p.background.paper,
        "--pt-surface-1": p.surface[1],
        "--pt-surface-2": p.surface[2],
        "--pt-surface-3": p.surface[3],
        "--pt-border-soft": p.border.soft,
        "--pt-border-default": p.border.default,
        "--pt-border-strong": p.border.strong,
        "--pt-text-primary": p.text.primary,
        "--pt-text-secondary": p.text.secondary,
        "--pt-text-disabled": p.text.disabled,
        "--pt-text-inverse": p.text.inverse,
        "--pt-primary": p.primary.main,
        "--pt-primary-light": p.primary.light,
        "--pt-primary-dark": p.primary.dark,
        "--pt-secondary": p.secondary.main,
        "--pt-success": p.success.main,
        "--pt-warning": p.warning.main,
        "--pt-error": p.error.main,
        "--pt-info": p.info.main,
        "--pt-radius-sm": `${radius.sm}px`,
        "--pt-radius-md": `${radius.md}px`,
        "--pt-radius-lg": `${radius.lg}px`,
        "--pt-radius-xl": `${radius.xl}px`,
        "--pt-radius-pill": `${radius.pill}px`,
        "--pt-radius-hero": "24px",
        "--pt-page-padding-top": `${pagePadding}px`,
        "--pt-shadow-1": shadows[1],
        "--pt-shadow-2": shadows[2],
        "--pt-shadow-3": shadows[3],
        "--pt-shadow-4": shadows[4],
        "--pt-shadow-5": shadows[5],
        "--pt-shadow-elevated": shadowElevated,
        "--pt-shadow-glow-sky": glow.sky,
        "--pt-shadow-glow-emerald": glow.emerald,
        "--pt-shadow-glow-hero": glow.hero,
        "--pt-shadow-glow-error": glow.error,
        "--pt-gradient-sky": gradients.sky,
        "--pt-gradient-emerald": gradients.emerald,
        "--pt-gradient-hero": gradients.hero,
        "--pt-gradient-sky-soft": gradients.skySoft,
        "--pt-gradient-emerald-soft": gradients.emeraldSoft,
        "--pt-gradient-pink": gradients.pink,
        "--pt-gradient-pink-soft": gradients.pinkSoft,
        "--pt-gradient-hero-pink": gradients.heroPink,
        "--pt-blur-glass": glass.blur,
        "--pt-blur-soft": glass.blurSoft,
        "--pt-transition": transitions.base,
        "--pt-transition-spring": transitions.spring,
        "--pt-ease-out-expo": transitions.easeOutExpo,
        "--pt-badge-success-bg": sb.success.bg,
        "--pt-badge-success-fg": sb.success.fg,
        "--pt-badge-warning-bg": sb.warning.bg,
        "--pt-badge-warning-fg": sb.warning.fg,
        "--pt-badge-danger-bg": sb.danger.bg,
        "--pt-badge-danger-fg": sb.danger.fg,
        "--pt-badge-info-bg": sb.info.bg,
        "--pt-badge-info-fg": sb.info.fg,
        "--pt-badge-neutral-bg": sb.neutral.bg,
        "--pt-badge-neutral-fg": sb.neutral.fg,
    };
}
